import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Trash2, Image as ImageIcon, X, Loader2, Upload, Link as LinkIcon } from 'lucide-react';
import { PortfolioItem } from '../../../types';
import { compressImage } from '../../../lib/imageUtils';
import { dbService } from '../../../services/dbService';

interface BarberPortfolioManagerProps {
  userId: string;
}

export const BarberPortfolioManager = ({ userId }: BarberPortfolioManagerProps) => {
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [uploadType, setUploadType] = useState<'upload' | 'url'>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [newItem, setNewItem] = useState({
    imageUrl: '',
    title: '',
    description: ''
  });

  useEffect(() => {
    if (!userId) return;
    const unsubscribe = dbService.onPortfolioChange(userId, (items) => {
      setPortfolio(items as PortfolioItem[]);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [userId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('jpeg')) {
      alert('Mohon pilih file JPEG');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const compressed = await compressImage(reader.result as string);
      setNewItem({ ...newItem, imageUrl: compressed });
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!newItem.imageUrl || !userId) return;
    
    setIsUploading(true);
    const item: PortfolioItem = {
      id: Math.random().toString(36).substr(2, 9),
      imageUrl: newItem.imageUrl,
      title: newItem.title,
      description: newItem.description,
      createdAt: new Date().toISOString()
    };

    await dbService.addPortfolioItem(userId, item);
    
    setNewItem({ imageUrl: '', title: '', description: '' });
    setIsAdding(false);
    setIsUploading(false);
    setUploadType('upload');
  };

  const handleRemove = async (id: string) => {
    if (!userId) return;
    if (confirm('Hapus karya ini dari portofolio?')) {
      await dbService.removePortfolioItem(userId, id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Portofolio Karya</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Tampilkan hasil cukuran terbaik Anda</p>
        </div>
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
        >
          <Plus className="w-4 h-4" />
          Tambah Karya
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {portfolio.map((item) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              key={item.id}
              className="relative aspect-square rounded-2xl overflow-hidden group border border-slate-100"
            >
              <img 
                src={item.imageUrl} 
                alt={item.title} 
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                {item.title && <p className="text-white text-[10px] font-black uppercase truncate pr-8">{item.title}</p>}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(item.id)}
                className="absolute top-2 right-2 p-2 bg-red-500/90 text-white rounded-lg hover:bg-red-600 shadow-lg transition-all active:scale-90 z-10"
                title="Hapus gambar"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>

        {portfolio.length === 0 && !isAdding && (
          <div className="col-span-2 py-12 border-2 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center text-slate-400">
            <ImageIcon className="w-8 h-8 mb-3 opacity-20" />
            <p className="text-[10px] font-black uppercase tracking-widest">Belum ada karya</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Tambah Karya Baru</h4>
              <button 
                type="button" 
                onClick={() => {
                  setIsAdding(false);
                  setNewItem({ imageUrl: '', title: '', description: '' });
                }}
                className="p-1 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setUploadType('upload')}
                className={`flex-1 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all border ${
                  uploadType === 'upload' 
                  ? 'bg-slate-900 text-white border-slate-900' 
                  : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'
                }`}
              >
                Upload JPEG
              </button>
              <button
                type="button"
                onClick={() => setUploadType('url')}
                className={`flex-1 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all border ${
                  uploadType === 'url' 
                  ? 'bg-slate-900 text-white border-slate-900' 
                  : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'
                }`}
              >
                URL Gambar
              </button>
            </div>

            <div className="space-y-4">
              {uploadType === 'upload' ? (
                <div>
                  <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">File JPEG</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/jpeg"
                    className="hidden"
                  />
                  {!newItem.imageUrl ? (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all bg-white"
                    >
                      {isUploading ? (
                        <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400" />
                          <span className="text-[10px] font-bold text-slate-500">Pilih File JPEG</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="relative h-48 rounded-2xl overflow-hidden border border-slate-200 transition-all group">
                      <img src={newItem.imageUrl} className="w-full h-full object-cover" alt="Preview" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setNewItem({ ...newItem, imageUrl: '' })}
                          className="bg-white text-slate-900 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95"
                        >
                          Ganti Gambar
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewItem({ ...newItem, imageUrl: '' })}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg shadow-lg"
                        title="Hapus"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">URL Gambar</label>
                  <div className="relative">
                    <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={newItem.imageUrl}
                      onChange={(e) => setNewItem({ ...newItem, imageUrl: e.target.value })}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs font-bold focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Judul (Opsional)</label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Classic Fade"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-bold focus:border-emerald-500 outline-none transition-all"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newItem.imageUrl || isUploading}
                className="w-full bg-slate-900 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50 transition-all shadow-xl shadow-slate-900/10"
              >
                Konfirmasi Tambah
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
